/* eslint-disable no-throw-literal */
import * as axiosStatic from "axios";
import { AxiosRequestConfig, AxiosResponse } from "axios";
import fetchJsonp from "fetch-jsonp";
import { isNullOrEmpty } from "../../components/shared/common/util";
import { ApplicationConstants } from "../../models/ApplicationConstants";
import { SessionStorageConsts } from "../../models/SessionStorageConsts";

/**
 * Base wrapper implementation for axios that does not provide an implementation for the passthrough to axios.
 * @class
 */
export class AxiosWrapperBase {
    /** axios instance to call through to.
     */
    private axios: axiosStatic.AxiosStatic;

    /**
     * Initializes a new instance of the `AxiosWrapperBase` class.
     * @constructor
     * @param {axiosStatic.AxiosStatic} axios Axios service instance.
     */
    constructor(axios?: axiosStatic.AxiosStatic) {
        this.axios = (axios || axiosStatic) as axiosStatic.AxiosStatic;
    }

    /**
     * Method to send any type of http call (get/post/put/etc.).
     * @method
     * @param config {axiosStatic.RequestOptions} The request detail.
     * @returns {Promise<AxiosResponse>} The result of the call.
     */
    public sendRequest(config: AxiosRequestConfig): Promise<AxiosResponse> {
        if (!config) {
            throw "config must not be null";
        }

        let headerConfig = {};
        const isAuthorizationEnabled = process.env.REACT_APP_AUTHORIZATION_ENABLED === "true";

        // Check if this is an Azure Search request
        const isAzureSearchRequest = config.headers &&
            (config.headers['X-Azure-Search'] === 'true' &&
                config.headers['Authorization']?.toString().startsWith('Bearer '));

        // Check if access token is already provided in headers
        const hasExistingAccessToken = config.headers &&
            (config.headers['Authorization'] || config.headers['authorization']);

        // Prepare headers after ensuring token is refreshed
        // Prepare base headers
        const baseHeaders = {
            CorrelationId: sessionStorage.getItem(SessionStorageConsts.correlationId),
            UserEmail: sessionStorage.getItem(SessionStorageConsts.userEmail),
        };

        // Add authorization header only if needed and not already present
        const authHeaders: any = {};
        const pcUserToken = sessionStorage.getItem(SessionStorageConsts.nextGenAPIPCUserToken);
        if (isAuthorizationEnabled &&
            !hasExistingAccessToken &&
            !isAzureSearchRequest &&
            pcUserToken) {
            authHeaders['Authorization'] = `Bearer ${pcUserToken}`;
        }

        // Merge headers: existing config headers take priority
        Object.assign(headerConfig, baseHeaders, authHeaders, config.headers);

        config = {
            ...config,
            headers: headerConfig,
        };

        this.onRequest(config);
        let response = this.axios(config) as Promise<AxiosResponse>;

        this.onResponse(response);
        return response;
    }


    /**
     * Convenient alias for doing a get service call.
     * @method
     * @param url {string} The url to make the service call to.
     * @param config {AxiosRequestConfig} Additional settings for the service call.
     * @returns {Promise<AxiosResponse>} The result of the call.
     */
    public get<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.callWithMergedConfig("get", url, config);
    }

    /**
     * Convenient alias for doing a delete service call.
     * @method
     * @param url {string} The url to make the service call to.
     * @param config {AxiosRequestConfig} Additional settings for the service call.
     * @returns {Promise<AxiosResponse>} The result of the call.
     */
    public delete(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse> {
        return this.callWithMergedConfig("delete", url, config);
    }

    /**
     * Convenient alias for doing a head service call.
     * @method
     * @param url {string} The url to make the service call to.
     * @param config {AxiosRequestConfig} Additional settings for the service call.
     * @returns {Promise<AxiosResponse>} The result of the call.
     */
    public head(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse> {
        return this.callWithMergedConfig("head", url, config);
    }

    /**
     * Convenient alias for doing a post service call.
     * @method
     * @param url {string} The url to make the service call to.
     * @param data {any} The data to send as the body of the request.
     * @param config {AxiosRequestConfig} Additional settings for the service call.
     * @returns {Promise<AxiosResponse>} The result of the call.
     */
    public post(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse> {
        return this.callWithMergedConfigWithData("post", url, data, config);
    }

    /**
     * Convenient alias for doing a put service call.
     * @method
     * @param url {string} The url to make the service call to.
     * @param data {any} The data to send as the body of the request.
     * @param config {AxiosRequestConfig} Additional settings for the service call.
     * @returns {Promise<AxiosResponse>} The result of the call.
     */
    public put(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse> {
        return this.callWithMergedConfigWithData("put", url, data, config);
    }

    /**
     * Convenient alias for doing a patch service call.
     * @method
     * @param url {string} The url to make the service call to.
     * @param data {any} The data to send as the body of the request.
     * @param config {AxiosRequestConfig} Additional settings for the service call.
     * @returns {Promise<AxiosResponse>} The result of the call.
     */
    public patch(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse> {
        return this.callWithMergedConfigWithData("patch", url, data, config);
    }

    /**
     * Overridable method to do pre-processing before the request is sent.
     * @method
     * @param config {axiosStatic.RequestOptions} The request detail.
     */
    protected onRequest(config: AxiosRequestConfig): void { } // tslint:disable-line

    /**
     * Overridable method to do post-processing after the response is received.
     * On a 401 Unauthorized response, checks whether useTokenExpiryMonitor has already
     * written a refreshed token to sessionStorage (i.e. the token was replaced between
     * the time this request was built and when it returned). If a newer token is present,
     * the request is retried once with the updated Authorization header so the user is
     * never interrupted by a token expiry mid-session. When no newer token exists in
     * sessionStorage, the backend refresh endpoint is called proactively so both the
     * access token and expiry are updated before the retry.
     * @method
     * @param result {Promise<AxiosResponse>} The response detail.
     * @returns {Promise<AxiosResponse>} The result of the call with any required changes added.
     */
    protected onResponse(result: Promise<AxiosResponse>): Promise<AxiosResponse> {
        return result.catch(async (error) => {
            const status = error?.response?.status;
            const originalConfig: AxiosRequestConfig & { _retried?: boolean } = error?.config;

            if (status === 401 && originalConfig && !originalConfig._retried) {
                originalConfig._retried = true;

                const sentToken = (originalConfig.headers?.['Authorization'] as string | undefined)
                    ?.replace('Bearer ', '');

                // Check whether useTokenExpiryMonitor has already stored a newer token.
                let latestToken = sessionStorage.getItem(SessionStorageConsts.nextGenAPIPCUserToken);

                if (!latestToken || latestToken === sentToken) {
                    // No newer token in sessionStorage — proactively call the backend refresh
                    // endpoint so both the access token and the expiry value are updated.
                    const tokenEndpoint = process.env.REACT_APP_RSLR_ACCESS_TOKEN;
                    if (tokenEndpoint) {
                        try {
                            const refreshResponse = await fetch(tokenEndpoint, {
                                method: 'GET',
                                credentials: 'include',
                            });
                            if (refreshResponse.ok) {
                                const headerExpiresOn = refreshResponse.headers.get('X-Token-ExpiresOn') ?? '';
                                const tokenJson = await refreshResponse.json();
                                const newToken: string = tokenJson?.AccessToken ?? tokenJson?.accessToken ?? '';
                                const expiresOn: string = headerExpiresOn || tokenJson?.ExpiresOn || tokenJson?.expiresOn || '';

                                if (newToken) {
                                    sessionStorage.setItem(SessionStorageConsts.nextGenAPIPCUserToken, newToken);
                                    latestToken = newToken;
                                }
                                if (expiresOn) {
                                    sessionStorage.setItem(SessionStorageConsts.userTokenExpiry, expiresOn);
                                }
                            }
                        } catch {
                            // Refresh attempt failed — fall through and reject the original error
                        }
                    }
                }

                if (latestToken && latestToken !== sentToken) {
                    originalConfig.headers = {
                        ...originalConfig.headers,
                        Authorization: `Bearer ${latestToken}`,
                    };
                    return this.axios(originalConfig) as Promise<AxiosResponse>;
                }
            }

            return Promise.reject(error);
        });
    }

    /**
     * Merge the given method and url into the given config object and call through to axios.
     * @method
     * @param method {string} The type of http call to make.
     * @param url {string} The url to make the service call to.
     * @param config {AxiosRequestConfig} The request detail.
     * @returns {Promise<AxiosResponse>} The combined configuration.
     */
    private callWithMergedConfig(method: string, url: string, config?: AxiosRequestConfig): Promise<AxiosResponse> {
        var mergedConfig = Object.assign({}, config, {
            method: method,
            url: url
        });

        return this.sendRequest(mergedConfig);
    }

    /**
     * Merge the given method, url and data into the given config object and call through to axios.
     * @method
     * @param method {string} The type of http call to make.
     * @param url {string} The url to make the service call to.
     * @param data {any} The data to send as the body of the request.
     * @param config {AxiosRequestConfig} The request detail.
     * @returns {Promise<AxiosResponse>} The combined configuration.
     */
    private callWithMergedConfigWithData(method: string, url: string, data: any, config?: AxiosRequestConfig): Promise<AxiosResponse> {

        if (!url) {
            throw "url must not be null";
        }

        var mergedConfig = Object.assign({}, config, {
            method: method,
            url: url,
            data: data
        });

        return this.sendRequest(mergedConfig);
    }
}